"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireWildcard(require("react"));
var _Dropdown = _interopRequireDefault(require("react-bootstrap/Dropdown"));
var _propTypes = _interopRequireDefault(require("prop-types"));
function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && Object.prototype.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
const coherentItems = items => items.map(item => typeof item === 'string' ? {
  label: item,
  value: item
} : item);
const getItemByKey = (items, key) => key && coherentItems(items).find(item => item.value === key);
const getItemByLabel = (items, label) => label && coherentItems(items).find(item => item.label.substring(0, label.length).toLowerCase() === label.toLowerCase());

/**
 * A Higher Order Component (HOC) that combines Dropdown, Dropdown.Toggle,
 * Dropdown.Menu, and Dropdown.Item to create a simple-looking dropdown that
 * supports type-to-select behavior.
 *
 * ## Data
 *
 * The prop `data` accepts two different types of data structures:
 *
 *  1. A list of string literals:
 *
 *         ['One', 'Two', 'Three']
 *
 *  2. A list of objects with 'label' and 'value' properties:
 *
 *         [
 *           { label: '', value: ''},
 *           { label: '', value: ''}
 *         ]
 *
 * ## Events
 *
 * This component will fire the `onSelect` event when a new item is selected
 * from the Dropdown. Due to the nature of the type to select behavior, no
 * other events are supported on inner components (Toggle, Menu, or Item) at
 * this time.
 *
 * ## Forms
 *
 * This component can be added to a regular web form. It will update the "value"
 * attribute of the Dropdown.Toggle (a button) to the selected value. When used
 * in a form, set the "name" of the field via the `toggleName` prop.
 *
 * ## Default Value
 *
 * To pre-populate the dropdown with a selection, pass the `defaultSearchValue`
 * prop with the label of the matching data item.
 *
 * ## Extra Props
 *
 * Additional props may be passed to the component, and they will be passed
 * on to the parent Dropdown component.
 */
const DropdownSelect = allProps => {
  const {
    data,
    defaultLabel,
    defaultSearchValue,
    id,
    onSelect,
    toggleDisabled,
    toggleId,
    toggleName,
    ...props
  } = allProps;

  // Local state:
  //  - active key
  //  - label
  //  - menu shown
  //  - previous label
  //  - search value
  const [activeKey, setActiveKey] = (0, _react.useState)(null);
  const [label, setLabel] = (0, _react.useState)(defaultLabel);
  const [menuShow, setMenuShow] = (0, _react.useState)(false);
  const [previousLabel, setPreviousLabel] = (0, _react.useState)(defaultLabel);
  const [searchValue, setSearchValue] = (0, _react.useState)(defaultSearchValue);
  const activeRef = /*#__PURE__*/(0, _react.createRef)(null);

  // set the label, search value, and active key all at once
  const reset = (0, _react.useCallback)(item => {
    const {
      label = defaultLabel,
      value = null
    } = item || {};
    setLabel(label);
    setSearchValue(label);
    setActiveKey(value);
    setPreviousLabel(null);
  }, [defaultLabel]);

  // Handle 'select' events when an item is selected in the Dropdown
  const onSelectHandler = (0, _react.useCallback)((key, e) => {
    const item = getItemByKey(data, key);
    reset(item);
    if (onSelect) onSelect(key, e);
  }, [data, onSelect]);

  // Handle 'toggle' events on the Dropdown
  const onToggleHandler = (0, _react.useCallback)((isOpen, _, _ref) => {
    let {
      source
    } = _ref;
    if (isOpen) {
      setPreviousLabel(label);
      setActiveKey(null);
    } else if (source === 'rootClose' && previousLabel !== null) {
      const item = getItemByLabel(data, previousLabel);
      reset(item);
    }
    setMenuShow(isOpen);
  }, [data, label, previousLabel]);

  // keyPress values to ignore
  const ignoreKeyList = [' ', 'Alt', 'ArrowLeft', 'ArrowRight', 'Backspace', 'CapsLock', 'Control', 'Enter', 'Meta', 'Shift', 'Tab'];

  // Handle 'keyDown' events on the Dropdown
  //
  // `useCallback` is a hook that will re-create this handler anytime a
  // part of the state that the logic depends on changes.
  const onKeyDownHandler = (0, _react.useCallback)(e => {
    const isUnexpectedKey = ignoreKeyList.find(key => key === e.key);
    if (!isUnexpectedKey && menuShow) {
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        setActiveKey(null);
        setSearchValue(null);
      } else if (e.key === 'Escape') {
        const item = getItemByLabel(data, previousLabel);
        reset(item);
      } else if (!searchValue) {
        setSearchValue(e.key);
      } else {
        setSearchValue(searchValue + e.key);
      }
    }
  },
  // dependent bits of state used in the callback
  [data, previousLabel, searchValue, menuShow]);

  // Whenever the search value changes, find the item that matches the label
  // and set that item as "active" (selected) in the menu.
  (0, _react.useEffect)(() => {
    const item = getItemByLabel(data, searchValue);
    if (item) {
      setLabel(item.label);
      setActiveKey(item.value);
    } else {
      setSearchValue(searchValue?.slice(-1));
    }
  }, [data, searchValue]);

  // This hook is invoked after the refs are set, because "it fires
  // synchronously after all DOM mutations"
  // (see: https://reactjs.org/docs/hooks-reference.html#uselayouteffect)
  (0, _react.useLayoutEffect)(() => {
    const activeElement = activeRef.current;
    if (activeElement) {
      activeElement.parentNode.scrollTop = activeElement.offsetTop;
      activeElement.focus();
    }
  }, [activeRef]);
  return /*#__PURE__*/_react.default.createElement(_Dropdown.default, _extends({
    className: "select-dropdown-simple",
    id: id,
    onKeyDown: onKeyDownHandler,
    onSelect: onSelectHandler,
    onToggle: onToggleHandler,
    tabIndex: "-1"
  }, props), /*#__PURE__*/_react.default.createElement(_Dropdown.default.Toggle, {
    className: "dropdown-select btn-secondary",
    disabled: toggleDisabled,
    id: toggleId,
    name: toggleName,
    value: activeKey
  }, label), /*#__PURE__*/_react.default.createElement(_Dropdown.default.Menu, {
    role: "listbox"
  }, coherentItems(data).map((_ref2, index) => {
    let {
      label,
      value,
      ...itemProps
    } = _ref2;
    return /*#__PURE__*/_react.default.createElement(_Dropdown.default.Item, _extends({
      active: activeKey === value,
      className: "dropdown-item-select",
      eventKey: value,
      id: `${id}-item-select-${index}`,
      key: value,
      ref: activeKey === value ? activeRef : null,
      role: "option"
    }, itemProps), label);
  })));
};
DropdownSelect.propTypes = {
  /** The initial label of the component. */
  defaultLabel: _propTypes.default.string,
  /** The initial search value of the component. */
  defaultSearchValue: _propTypes.default.string,
  /** The data for the items in the menu. */
  data: _propTypes.default.array.isRequired,
  /** The identifier of this component. */
  id: _propTypes.default.string,
  /** The callback invoked when an item is selected. */
  onSelect: _propTypes.default.func,
  /** A flag to disable the dropdown. */
  toggleDisabled: _propTypes.default.bool,
  /** The ID of the toggle button. */
  toggleId: _propTypes.default.string,
  /** The form's field name of the toggle button. */
  toggleName: _propTypes.default.string
};
DropdownSelect.defaultProps = {
  data: [],
  toggleDisabled: false
};
var _default = exports.default = DropdownSelect;